import { useState, useEffect } from "react";
import axios from "axios";
import { Storefront, Star, ArrowSquareOut, Package } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MP_COLOR = {
  amazon: "bg-amber-100 text-amber-700", walmart: "bg-blue-100 text-blue-700",
  etsy: "bg-orange-100 text-orange-700", shopify: "bg-emerald-100 text-emerald-700",
  tiktok_shop: "bg-zinc-900 text-white", temu: "bg-orange-100 text-orange-700",
  target: "bg-rose-100 text-rose-700",
};

const Stat = ({ label, value }) => (
  <div className="text-center">
    <div className="text-lg font-black text-violet-700" data-testid={`es-stat-${label}`}>{value ?? "—"}</div>
    <div className="text-[9px] uppercase tracking-wide text-zinc-400">{label}</div>
  </div>
);

const ProductCard = ({ p }) => (
  <div className="rounded-xl bg-white border border-zinc-100 overflow-hidden flex flex-col" data-testid="es-product-card">
    <div className="h-32 bg-zinc-50 flex items-center justify-center overflow-hidden">
      {p.image
        ? <img src={p.image} alt={p.product_name} className="h-full w-full object-contain" loading="lazy" />
        : <Package size={28} className="text-zinc-300" />}
    </div>
    <div className="p-3 flex flex-col gap-1 flex-1">
      <div className="text-[11px] font-semibold text-zinc-800 line-clamp-2 leading-snug">{p.product_name}</div>
      {p.brand && <div className="text-[10px] text-zinc-400">{p.brand}</div>}
      <div className="flex items-center gap-2 mt-auto pt-1">
        {p.price != null && <span className="text-sm font-black text-zinc-900">${p.price}</span>}
        {p.rating != null && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-bold">
            <Star size={10} weight="fill" />{p.rating}
          </span>
        )}
        {p.review_count != null && <span className="text-[10px] text-zinc-400">{p.review_count.toLocaleString()} rev</span>}
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${MP_COLOR[p.marketplace] || "bg-zinc-100 text-zinc-500"}`}>{p.marketplace}</span>
        {p.url && (
          <a href={p.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:underline"
            data-testid="es-open-product">
            Open <ArrowSquareOut size={11} />
          </a>
        )}
      </div>
    </div>
  </div>
);

const OpportunityBlock = ({ item }) => {
  const s = item.summary || {};
  return (
    <div className="rounded-2xl bg-white/85 border border-violet-100 p-5" data-testid={`es-opp-${item.cluster_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-zinc-900">{item.cluster_name || item.cluster_id}</h3>
          <div className="text-[11px] text-violet-500 font-semibold">{item.market}</div>
          <p className="text-[12px] text-zinc-600 mt-1 max-w-2xl">{s.sentence}</p>
          {s.top_brands?.length > 0 && (
            <div className="text-[11px] text-zinc-500 mt-1">Top brands: <b>{s.top_brands.join(", ")}</b></div>
          )}
        </div>
        <div className="flex gap-4">
          <Stat label="products" value={s.total_products} />
          <Stat label="avg price" value={s.avg_price != null ? `$${s.avg_price}` : null} />
          <Stat label="avg rating" value={s.avg_rating} />
          <Stat label="avg reviews" value={s.avg_review_count != null ? s.avg_review_count.toLocaleString() : null} />
        </div>
      </div>
      {(item.products || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
          {item.products.map((p, i) => <ProductCard key={i} p={p} />)}
        </div>
      )}
    </div>
  );
};

const ExistingSolutions = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/commerce/solutions/results`)
      .then(({ data }) => setData(data.results || []))
      .catch(() => setData([]));
  }, []);

  if (!data) return <div className="text-center py-16 text-zinc-400" data-testid="es-loading">Loading…</div>;

  if (data.length === 0) {
    return (
      <div className="text-center py-16 max-w-lg mx-auto" data-testid="es-empty">
        <Storefront size={40} className="mx-auto text-violet-300 mb-3" weight="duotone" />
        <div className="text-sm font-bold text-zinc-700 mb-1">No marketplace supply collected yet</div>
        <p className="text-[12px] text-zinc-500">
          Phase 5 answers one question — <b>“Who already sells this?”</b> It runs only on opportunities
          you mark <b>Build</b> in the Review Workspace, and only when you approve the scrape. Once it runs,
          competing products (image, price, rating, reviews) appear here as cards.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="commerce-existing-solutions">
      {data.map((item) => <OpportunityBlock key={item.cluster_id} item={item} />)}
    </div>
  );
};

export default ExistingSolutions;
